import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Phone,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLanguagePreference } from "@/hooks/useLanguagePreference";
import { LANGUAGE_LABELS } from "@/lib/i18n/languages";
import { INK, MUTED, LINE_SOFT, EASE } from "@/lib/marketing-theme";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSettingsAlert } from "@/hooks/useTenantSettingsAlert";
import { AuthModal } from "@/components/auth/AuthModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { BrandWordmark } from "@/components/BrandWordmark";

/** The brand wordmark — identical in every language, so not a translatable string. */
const WORDMARK = "coliviq";

/**
 * The header language control, rendered three times below (desktop bar,
 * narrow-screen bar, drawer header).
 *
 * This used to be `setLanguage(language === "en" ? "de" : "en")`, which sent
 * every language that is not English to German. Once COL-478 gave tenants six,
 * a French tenant who tapped the "FR" in this header got German and had no way
 * back — the header was the only language control on the tenant dashboard, and
 * it could not express four of the languages the dashboard speaks. It also
 * wrote nothing but localStorage, so the switch disagreed with Settings and,
 * worse, pinned the browser: LanguageProvider stops seeding from the account
 * once a browser holds an explicit choice.
 *
 * Both are fixed by going through `useLanguagePreference`, the same hook
 * Settings → Language uses. Two options — the public site, and every staff,
 * owner and operator account — keep the one-click flip they have always had.
 * More than two opens a menu: cycling six languages through a two-letter
 * button would take up to five clicks and never show what the next one gives.
 */
function LanguageSwitch({
  ghost = false,
  className,
  labelClassName,
  style,
}: {
  /** Match the surrounding bar: a bare <button> on desktop, ghost Button elsewhere. */
  ghost?: boolean;
  className?: string;
  labelClassName?: string;
  style?: React.CSSProperties;
}) {
  const { t } = useLanguage();
  const { language, options, choose } = useLanguagePreference();
  const code = language.toUpperCase();
  // The visible label is two letters, so name the control — but keep those two
  // letters in the accessible name. Dropping them (a bare "Language") both
  // hides the current value from a screen reader and breaks WCAG 2.5.3, which
  // wants a control's visible text to appear in its accessible name.
  const label = `${t("nav.language", "Language")}: ${code}`;

  const trigger = (onClick?: () => void, chevron = false) => {
    const body = (
      <>
        <span className={labelClassName}>{code}</span>
        {chevron && (
          <ChevronDown
            className="h-3 w-3 shrink-0 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180"
            aria-hidden
          />
        )}
      </>
    );
    return ghost ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        aria-label={label}
        className={cn("group gap-1", className)}
        style={style}
      >
        {body}
      </Button>
    ) : (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={cn(
          "group inline-flex items-center gap-1 transition-opacity hover:opacity-70",
          className
        )}
        style={style}
      >
        {body}
      </button>
    );
  };

  if (options.length <= 2) {
    const next = options.find((option) => option !== language) ?? language;
    return trigger(() => void choose(next));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger(undefined, true)}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-56 rounded-2xl border-border/60 p-1.5 shadow-xl"
      >
        {options.map((option) => {
          const active = option === language;
          return (
            <DropdownMenuItem
              key={option}
              onSelect={() => void choose(option)}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm",
                active && "font-semibold"
              )}
            >
              <Check
                className={cn(
                  "h-4 w-4 shrink-0 text-primary",
                  active ? "opacity-100" : "opacity-0"
                )}
                aria-hidden
              />
              {/*
                `lang` but deliberately no `dir`: العربية is a single RTL run,
                which the bidi algorithm already shapes correctly, and putting
                dir="rtl" on the row flips that row's layout alone — the check
                and the code jump sides and the Arabic label drifts to the far
                edge while the other five stay left. The container's direction
                mirrors the whole menu when the UI itself is Arabic, which is
                what should happen.
              */}
              <span lang={option} className="flex-1 truncate text-start">
                {LANGUAGE_LABELS[option]}
              </span>
              <span
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-wider tabular-nums",
                  active ? "text-primary" : "text-muted-foreground"
                )}
                aria-hidden
              >
                {option}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const Navigation = () => {
  const { language, t, isLoading } = useLanguage();
  const {
    user,
    session,
    hasStaffRole,
    isOwnerPortalOnly,
    isDemoDualViewAccount,
    isOperatorPortal,
    signOut,
    refreshRoles,
  } = useAuth();
  /** Prefer session — cleared synchronously on sign-out; avoids stale `user` showing Dashboard. */
  const isLoggedIn = Boolean(session?.user ?? user);
  // The demo account is owner-portal-only but deliberately keeps a tenant
  // view to switch into (COL-447), so it is the one account that must NOT be
  // redirected to the owner console. This branch was cut before that landed
  // on main; without the exception the demo's Dashboard goes to /admin.
  const dashboardHref =
    isOwnerPortalOnly && !isDemoDualViewAccount
      ? "/admin?tab=analytics"
      : "/dashboard";
  const location = useLocation();
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Tenant Settings lives here now instead of in the tenant dashboard's tab
  // strip. The modal itself stays inside Dashboard, so we deep-link into it
  // with ?settings=<tab> and Dashboard opens it (and strips the param).
  // Owner-portal-only accounts have no tenant dashboard, so no entry.
  // Called before the /admin early return below — hooks can't be conditional.
  const showTenantSettings =
    isLoggedIn && (!isOwnerPortalOnly || isDemoDualViewAccount);
  const settingsAlert = useTenantSettingsAlert(showTenantSettings);

  // Hide the public-site nav on every admin route — both V2 (/admin/<slug>)
  // and the legacy console (/admin?tab=...). Both now render V2AdminNavbar
  // as their primary chrome, so the marketing nav above it would just
  // duplicate the "go to home / concept / properties" links the admin
  // doesn't need while operating tenants.
  //
  // The OAuth bounce page at /admin/docusign/callback keeps the marketing
  // nav because it's a hand-off page that may briefly show before the
  // user is redirected back to a deep admin link, and we don't want a
  // bare/unbranded screen during that flicker.
  const isAdminRoute =
    (location.pathname === "/admin" ||
      location.pathname.startsWith("/admin/")) &&
    !location.pathname.startsWith("/admin/docusign");
  if (isAdminRoute) {
    return null;
  }

  // Labels are written out rather than read through `t()`: the DB-backed
  // strings behind nav.owners / nav.operators still hold the previous naming
  // ("Entwickler", "WG Vermieter") and a stored value wins over a fallback,
  // so t() would quietly put the old labels back.
  // Route mapping is deliberate and was easy to get backwards:
  // WG-Vermieter (management pitch) -> /operators, Projektpartner -> /owners.
  const de = language === "de";
  const navLinks = [
    { path: "/", label: de ? "Start" : "Home" },
    { path: "/rooms", label: de ? "WG-Zimmer" : "Rooms" },
    { path: "/owners", label: de ? "WG-Vermieter" : "For landlords" },
    { path: "/developers", label: de ? "Projektpartner" : "Project partners" },
    { path: "/concept", label: de ? "Über uns" : "About us" },
    { path: "/contact", label: de ? "Kontakt" : "Contact" },
  ];

  /** `user` can lag behind `session` right after sign-in, so read through both. */
  const authUser = session?.user ?? user;

  const settingsHref = `/dashboard?settings=${settingsAlert ?? "profile"}`;
  const settingsLabel = t(
    "nav.settings",
    language === "de" ? "Einstellungen" : "Settings"
  );

  const handleNavClick = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <nav
      className="fixed left-0 right-0 top-0 z-50 border-b bg-white"
      style={{ borderColor: LINE_SOFT }}
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-14">
        <div className="flex h-[72px] items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="flex shrink-0 items-center"
              aria-label={WORDMARK}
            >
              <BrandWordmark className="h-7 w-auto" />
            </Link>

            {/* Desktop navigation, to the draft: plain text, the active item in
              petrol at 600 with a sliding 2px rule. Only from `lg` up — at `md`
              the full bar overflowed and clipped the language/account
              controls, so narrow desktops keep the menu button. */}
            <div className="hidden items-center gap-[26px] lg:flex">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="relative whitespace-nowrap py-1 text-[14.5px] transition-colors"
                    style={{
                      color: isActive ? INK : MUTED,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        transition={{ duration: 0.35, ease: EASE }}
                        className="absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full"
                        style={{ background: INK }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Desktop Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSwitch
              className="px-1.5 text-[14px] font-semibold leading-none transition-opacity hover:opacity-70"
              style={{ color: MUTED }}
            />

            {isLoggedIn ? (
              <DropdownMenu
                onOpenChange={(open) => {
                  if (open) void refreshRoles();
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="relative flex h-9 items-center rounded-full px-5 text-white hover:opacity-90"
                    style={{ background: INK }}
                  >
                    <span>{t("nav.dashboard")}</span>
                    {/* Carries the Settings "action needed" signal that used to
                        badge the tab strip — otherwise it would be hidden
                        behind a closed dropdown. */}
                    {settingsAlert ? (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-background"
                        aria-hidden="true"
                      />
                    ) : null}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <Link to={dashboardHref}>
                    <DropdownMenuItem className="cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {t("nav.dashboard")}
                    </DropdownMenuItem>
                  </Link>
                  {hasStaffRole ? (
                    <Link to="/admin">
                      <DropdownMenuItem className="cursor-pointer">
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        {t("admin.nav.admin", "Admin")}
                      </DropdownMenuItem>
                    </Link>
                  ) : isOwnerPortalOnly ? (
                    <Link to="/admin?tab=analytics">
                      <DropdownMenuItem className="cursor-pointer">
                        <Building2 className="w-4 h-4 mr-2" />
                        {t("nav.ownerPortal", "Owner portal")}
                      </DropdownMenuItem>
                    </Link>
                  ) : isOperatorPortal ? (
                    <Link to="/admin?tab=analytics">
                      <DropdownMenuItem className="cursor-pointer">
                        <Building2 className="w-4 h-4 mr-2" />
                        {t("nav.operatorConsole", "Operator console")}
                      </DropdownMenuItem>
                    </Link>
                  ) : null}
                  {showTenantSettings ? (
                    <Link to={settingsHref}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Settings className="w-4 h-4 mr-2" />
                        {settingsLabel}
                        {settingsAlert ? (
                          <span
                            className="ml-auto w-2 h-2 rounded-full bg-destructive"
                            aria-label={t(
                              "nav.settingsActionNeeded",
                              language === "de"
                                ? "Aktion erforderlich"
                                : "Action needed"
                            )}
                          />
                        ) : null}
                      </DropdownMenuItem>
                    </Link>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive"
                    onSelect={() => {
                      void signOut();
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("auth.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setAuthModalOpen(true)}
                  className="px-2.5 text-[14px] font-semibold transition-opacity hover:opacity-70"
                  style={{ color: INK }}
                >
                  {t("auth.login")}
                </button>
                <Link
                  to="/rooms"
                  className="inline-flex h-9 items-center rounded-full px-4 text-[14px] font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                  style={{ background: INK }}
                >
                  {de ? "Zimmer finden" : "Find a room"}
                </Link>
              </>
            )}
          </div>

          {/* Mobile / narrow-desktop menu button */}
          <div className="lg:hidden flex items-center space-x-2">
            <LanguageSwitch
              ghost
              className="flex items-center"
              labelClassName="font-medium text-xs"
            />

            <Sheet
              open={sidebarOpen}
              onOpenChange={(open) => {
                setSidebarOpen(open);
                if (open) void refreshRoles();
              }}
            >
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">
                    {t("common.nav.openMenu", "Open menu")}
                  </span>
                </Button>
              </SheetTrigger>
              {/* Drops down from the top and takes over the whole screen.
                  `[&>button]:top-7` re-centres the sheet's own close button
                  against the h-[72px] banner below without touching the shared
                  Sheet component. */}
              <SheetContent
                side="top"
                className="h-[100dvh] w-full p-0 [&>button]:right-4 [&>button]:top-7"
                a11yTitle="Menu"
              >
                <div className="flex h-full flex-col">
                  {/* CoLiviq header banner – mirrors the nav bar it replaces.
                      The language toggle lives here too: the panel covers the
                      whole screen, so the one in the bar is unreachable while
                      the menu is open. `pr-16` keeps clear of the close button. */}
                  <div className="flex h-[72px] shrink-0 items-center justify-between gap-2 border-b border-border px-5 pr-16 sm:px-8 sm:pr-16">
                    <div className="flex items-center">
                      <BrandWordmark className="h-7 w-auto" />
                    </div>
                    <LanguageSwitch
                      ghost
                      className="h-8"
                      labelClassName="text-xs font-medium leading-none"
                    />
                  </div>

                  {/* Navigation Links – large type, no icons */}
                  <nav className="flex-1 overflow-y-auto px-5 py-8 sm:px-8">
                    <div className="flex flex-col">
                      {navLinks.map((link) => {
                        const isActive = location.pathname === link.path;
                        return (
                          <SheetClose key={link.path} asChild>
                            <button
                              onClick={() => handleNavClick(link.path)}
                              className={`py-4 text-left text-2xl font-semibold transition-colors ${
                                isActive
                                  ? "text-primary"
                                  : "text-foreground hover:text-primary"
                              }`}
                            >
                              {link.label}
                            </button>
                          </SheetClose>
                        );
                      })}
                    </div>

                    {/* Phone number (display only) */}
                    <div className="mt-6 flex select-all items-center gap-3 text-foreground cursor-default">
                      <Phone className="h-5 w-5 shrink-0 animate-phone-ring text-primary" />
                      <span className="font-semibold">+49 89 54193354</span>
                    </div>
                  </nav>

                  {/* Account actions – pinned to the bottom */}
                  <div className="shrink-0 border-t border-border px-5 py-4 sm:px-8">
                    {isLoggedIn ? (
                      <>
                        <div className="px-2 pb-2">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {authUser?.user_metadata?.name ||
                              authUser?.email?.split("@")[0] ||
                              "User"}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {authUser?.email || ""}
                          </p>
                        </div>
                        <SheetClose asChild>
                          <button
                            onClick={() => {
                              handleNavClick(dashboardHref);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                          >
                            <LayoutDashboard className="h-4 w-4 shrink-0" />
                            {t("nav.dashboard")}
                          </button>
                        </SheetClose>
                        {hasStaffRole ? (
                          <SheetClose asChild>
                            <button
                              onClick={() => {
                                handleNavClick("/admin");
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                            >
                              <ShieldCheck className="h-4 w-4 shrink-0" />
                              {t("admin.nav.admin", "Admin")}
                            </button>
                          </SheetClose>
                        ) : isOwnerPortalOnly ? (
                          <SheetClose asChild>
                            <button
                              onClick={() => {
                                handleNavClick("/admin?tab=analytics");
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                            >
                              <Building2 className="h-4 w-4 shrink-0" />
                              {t("nav.ownerPortal", "Owner portal")}
                            </button>
                          </SheetClose>
                        ) : isOperatorPortal ? (
                          <SheetClose asChild>
                            <button
                              onClick={() => {
                                handleNavClick("/admin?tab=analytics");
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                            >
                              <Building2 className="h-4 w-4 shrink-0" />
                              {t("nav.operatorConsole", "Operator console")}
                            </button>
                          </SheetClose>
                        ) : null}
                        {showTenantSettings ? (
                          <SheetClose asChild>
                            <button
                              onClick={() => {
                                handleNavClick(settingsHref);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                            >
                              <Settings className="h-4 w-4 shrink-0" />
                              {settingsLabel}
                              {settingsAlert ? (
                                <span
                                  className="ml-auto w-2 h-2 rounded-full bg-destructive"
                                  aria-label={t(
                                    "nav.settingsActionNeeded",
                                    language === "de"
                                      ? "Aktion erforderlich"
                                      : "Action needed"
                                  )}
                                />
                              ) : null}
                            </button>
                          </SheetClose>
                        ) : null}
                        <button
                          onClick={() => {
                            void signOut();
                            setSidebarOpen(false);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg px-2 py-3 text-left font-medium text-destructive transition-colors hover:bg-muted"
                        >
                          <LogOut className="h-4 w-4 shrink-0" />
                          {t("auth.logout")}
                        </button>
                      </>
                    ) : (
                      <SheetClose asChild>
                        <button
                          onClick={() => {
                            setSidebarOpen(false);
                            setAuthModalOpen(true);
                          }}
                          className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                        >
                          {t("auth.login")}
                        </button>
                      </SheetClose>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </nav>
  );
};

export default Navigation;
